import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ClosedDayBookingFormModal } from './ClosedDayBookingFormModal';
import { getClosedDayBookingColumns } from './closedDayBookingColumns';
import { useClosedDayBookings } from '../../hooks/useClosedDayBookings';
import type { ClosedDayBookingRequest, ClosedDayBookingResponse } from '../../api/generated';
import {
  BookingListSection,
  DeleteBookingModal,
  isSingleOccurrenceTime,
  useBookingList
} from '../Bookings/shared';
import { formatClosedDayPeriod } from './closedDayTime';

const EMPTY = '—';
const TABLE_MIN_WIDTH = 640;
const KEY_PREFIX = 'closed-day';

export const ClosedDayBookings = () => {
  const { t } = useTranslation();
  const { locationId: routeLocationId } = useParams<{ locationId: string }>();
  const {
    bookings,
    isLoading,
    loadError,
    locationId,
    refresh,
    createBooking,
    updateBooking,
    deleteBooking
  } = useClosedDayBookings(routeLocationId ?? null);

  const list = useBookingList<ClosedDayBookingResponse, ClosedDayBookingRequest>({
    keyPrefix: KEY_PREFIX,
    createBooking,
    updateBooking,
    deleteBooking
  });

  const columns = useMemo(
    () =>
      getClosedDayBookingColumns(t, {
        onEdit: list.openEdit,
        onDelete: list.requestDelete
      }),
    [t, list.openEdit, list.requestDelete]
  );

  const periodLabelFor = (booking: ClosedDayBookingResponse): string =>
    isSingleOccurrenceTime(booking.time) ? formatClosedDayPeriod(booking.time) : EMPTY;

  return (
    <BookingListSection
      keyPrefix={KEY_PREFIX}
      isLoading={isLoading}
      errorMessage={loadError}
      locationId={locationId}
      columns={columns}
      bookings={bookings}
      minWidth={TABLE_MIN_WIDTH}
      onAdd={list.openCreate}
      onRetry={() => {
        refresh();
      }}
    >
      {list.isFormOpen && (
        <ClosedDayBookingFormModal
          booking={list.editingBooking}
          onSave={(payload) => {
            list.handleSave(payload);
          }}
          onClose={list.closeForm}
        />
      )}

      {list.deleteTarget && (
        <DeleteBookingModal
          titleKey="closed-day-delete-title"
          confirmKey="closed-day-delete-confirm"
          messageKey="closed-day-delete-message"
          name={periodLabelFor(list.deleteTarget)}
          onConfirm={() => {
            list.handleConfirmDelete();
          }}
          onClose={list.cancelDelete}
        />
      )}
    </BookingListSection>
  );
};
